import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, isAfter, addHours, parseISO, isValid } from 'date-fns';
import { 
  Calendar, 
  Search, 
  Plus, 
  Filter, 
  ChevronLeft, 
  ChevronRight, 
  Users, 
  Clock, 
  MapPin, 
  BarChart3, 
  RefreshCw, 
  UserCheck,
  ChevronDown,
  ChevronUp,
  Edit,
  Trash2,
  X,
  Check,
  Grid,
  List,
  UserPlus,
  MoreVertical,
  Star,
  MessageSquare,
  Hash,
  UserMinus,
  CheckCircle,
  XCircle,
  AlertCircle,
  Shield,
  UserX,
  Eye,
  Download,
  Share2,
  MoreHorizontal,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Target,
  Activity,
  TrendingUp,
  PieChart,
  LineChart,
  Settings,
  Bell,
  MessageCircle,
  Send,
  Reply,
  Forward,
  Copy,
  Bookmark,
  Flag,
  Tag,
  AtSign,
  Globe,
  Home,
  Building,
  GraduationCap,
  Briefcase,
  Coffee,
  Gift,
  Cake,
  Music,
  Camera,
  Video,
  Mic,
  Baby,
  Crown,
  ArrowRight,
  Heart,
  Church
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { PermissionGuard, PermissionButton } from '@/components/PermissionGuard';
import { PERMISSIONS } from '@/lib/permissions.jsx';
import { addEvent, updateEvent, deleteEvent, getEvents } from '@/lib/data';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/lib/authContext';
import { locationService } from '@/lib/locationService';
import { eventReminderService } from '@/lib/eventReminderService';
import { formatName, getInitials } from '@/lib/utils/formatters';
import { getMobileScrollStyles, initializeMobileScrolling } from '@/lib/utils/mobileScrolling';
import { initializeMobileScrollTesting } from '@/lib/utils/mobileScrollTest';
import { AddVolunteerForm } from '@/components/events/AddVolunteerForm';
import { VolunteerList } from '@/components/events/VolunteerList';

export default function MobileEvents() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  
  // Check if we're viewing a specific event
  const isIndividualEvent = location.pathname !== '/events';
  const eventId = isIndividualEvent ? location.pathname.split('/events/')[1] : null;
  
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [timeWindowFilter, setTimeWindowFilter] = useState('all');
  const [volunteerFilter, setVolunteerFilter] = useState('all');
  const [sortField, setSortField] = useState('start_date');
  const [sortDirection, setSortDirection] = useState('asc');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [activeTab, setActiveTab] = useState('upcoming');
  const [showFilters, setShowFilters] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pullToRefreshY, setPullToRefreshY] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isCheckInDialogOpen, setIsCheckInDialogOpen] = useState(false);
  const [isVolunteerDialogOpen, setIsVolunteerDialogOpen] = useState(false);
  const [volunteerDialogEvent, setVolunteerDialogEvent] = useState(null);
  const [volunteerRefreshKey, setVolunteerRefreshKey] = useState(0);
  
  // Edit event states
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEditChoiceDialog, setShowEditChoiceDialog] = useState(false);
  const [eventToEdit, setEventToEdit] = useState(null);
  const [masterEventToEdit, setMasterEventToEdit] = useState(null);
  
  // Member states for check-in
  const [members, setMembers] = useState([]);
  const [alreadyRSVPMembers, setAlreadyRSVPMembers] = useState([]);
  const [suggestedMembers, setSuggestedMembers] = useState([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  
  // Location and groups for event form
  const [locations, setLocations] = useState([]);
  const [groups, setGroups] = useState([]);

  // Form data for event creation/editing
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    startDate: '',
    startTime: '09:00',
    endDate: '',
    endTime: '10:00',
    location: '',
    location_id: '',
    event_type: 'Other',
    track_attendance: false,
    allow_rsvp: false,
    attendance_type: 'check-in',
    recurring: false,
    recurrence_pattern: 'none',
    monthly_week: '',
    monthly_weekday: '',
    needs_volunteers: false,
    volunteer_roles: '',
    enable_reminders: false,
    reminder_type: 'sms',
    reminder_timing_value: 24,
    reminder_timing_unit: 'hours',
    reminder_target: 'all',
    reminder_target_groups: []
  });

  const eventTypes = [
    'Worship Service',
    'Bible Study or Class', 
    'Prayer Meeting',
    'Fellowship Gathering',
    'Potluck',
    'Youth Group',
    'Children\'s Ministry',
    'Men\'s Ministry',
    'Women\'s Ministry', 
    'Board Meeting',
    'Community Service',
    'Special Event',
    'Other'
  ];
  
  // Load locations
  const loadLocations = useCallback(async () => {
    try {
      const data = await locationService.getLocations();
      setLocations(data);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  }, []);

  // Load groups
  const loadGroups = useCallback(async () => {
    try {
      const data = await eventReminderService.getTargetGroups();
      setGroups(data);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  }, []);

  // Load events
  const loadEvents = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Get organization ID for the user
      const { data: orgUser } = await supabase
        .from('organization_users')
        .select('organization_id')
        .eq('user_id', user?.id)
        .limit(1)
        .maybeSingle();
      
      if (!orgUser?.organization_id) {
        throw new Error('User not associated with any organization');
      }
      
      // Load ALL events (past and future) for mobile view
      const { data: eventsData, error: eventsError } = await supabase
        .from('events')
        .select('*')
        .eq('organization_id', orgUser.organization_id)
        .order('start_date', { ascending: true });
      
      if (eventsError) throw eventsError;
      setEvents(eventsData || []);
      
      // If viewing individual event, find and set it
      if (isIndividualEvent && eventId && eventsData) {
        const event = eventsData.find(e => {
          // Try multiple matching strategies
          return e.id === eventId || 
                 e.slug === eventId || 
                 e.id?.toString() === eventId ||
                 e.slug?.toString() === eventId;
        });
        
        if (event) {
          setSelectedEvent(event);
        } else {
          toast({
            title: "Event not found",
            description: "The requested event could not be found",
            variant: "destructive"
          });
        }
      }
    } catch (error) {
      console.error('Error loading events:', error);
      toast({
        title: "Error",
        description: "Failed to load events",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, toast, isIndividualEvent, eventId]);

  // Load members for check-in
  const loadMembers = useCallback(async () => {
    try {
      const { data: orgUser } = await supabase
        .from('organization_users')
        .select('organization_id')
        .eq('user_id', user?.id)
        .limit(1)
        .maybeSingle();
      
      if (!orgUser?.organization_id) return;

      const { data, error } = await supabase
        .from('members')
        .select('*')
        .eq('organization_id', orgUser.organization_id)
        .in('status', ['active', 'visitor'])
        .order('firstname', { ascending: true });
      
      if (error) throw error;
      setMembers(data || []);
    } catch (error) {
      console.error('Error loading members:', error);
    }
  }, [user]);

  // Load RSVP members for selected event
  const loadRSVPMembers = useCallback(async (eventId) => {
    try {
      const { data, error } = await supabase
        .from('event_attendance')
        .select(`
          member_id,
          status,
          members!inner(*)
        `)
        .eq('event_id', eventId)
        .eq('status', 'attending');

      if (error) throw error;
      setAlreadyRSVPMembers(data?.map(item => item.members) || []);
    } catch (error) {
      console.error('Error loading RSVP members:', error);
      setAlreadyRSVPMembers([]);
    }
  }, []);

  // Load suggested frequent attendees
  const loadSuggestedMembers = useCallback(async (event, allMembers) => {
    try {
      if (!event.is_recurring || !event.event_type) {
        setSuggestedMembers([]);
        return;
      }

      const { data: orgUser } = await supabase
        .from('organization_users')
        .select('organization_id')
        .eq('user_id', user?.id)
        .limit(1)
        .maybeSingle();
      
      if (!orgUser?.organization_id) return;

      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

      // Get previous attendance for similar recurring events
      const { data: previousAttendance, error } = await supabase
        .from('event_attendance')
        .select(`
          member_id,
          events!inner(event_type, is_recurring, organization_id)
        `)
        .eq('events.organization_id', orgUser.organization_id)
        .eq('events.event_type', event.event_type)
        .eq('events.is_recurring', true)
        .gte('events.start_date', ninetyDaysAgo.toISOString())
        .eq('status', 'attending');

      if (error) {
        console.error('Error loading previous attendance:', error);
        setSuggestedMembers([]);
        return;
      }

      // Count attendance frequency per member
      const attendanceCount = {};
      previousAttendance?.forEach(attendance => {
        attendanceCount[attendance.member_id] = (attendanceCount[attendance.member_id] || 0) + 1;
      });

      // Sort members by attendance frequency
      const frequentAttendees = Object.entries(attendanceCount)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 10)
        .map(([memberId]) => allMembers.find(m => m.id === memberId))
        .filter(Boolean);

      // Filter out already checked-in members
      const alreadyCheckedIn = alreadyRSVPMembers.map(m => m.id);
      const suggestions = frequentAttendees.filter(member => !alreadyCheckedIn.includes(member.id));

      setSuggestedMembers(suggestions);
    } catch (error) {
      console.error('Error loading suggested members:', error);
      setSuggestedMembers([]);
    }
  }, [user, alreadyRSVPMembers]);

  // Handle member check-in
  const handleCheckIn = async (member) => {
    if (!selectedEvent) return;

    try {
      const { error } = await supabase
        .from('event_attendance')
        .upsert({
          event_id: selectedEvent.id,
          member_id: member.id,
          status: 'attending'
        });

      if (error) throw error;

      // Refresh RSVP members
      await loadRSVPMembers(selectedEvent.id);
      
      // Refresh suggested members to exclude newly checked-in member
      if (selectedEvent.is_recurring && members.length > 0) {
        await loadSuggestedMembers(selectedEvent, members);
      }
      
      toast({
        title: "Success",
        description: `${formatName(member.firstname, member.lastname)} checked in successfully`
      });
    } catch (error) {
      console.error('Error checking in member:', error);
      toast({
        title: "Error",
        description: "Failed to check in member",
        variant: "destructive"
      });
    }
  };

  // Handle member check-out (remove from event)
  const handleCheckOut = async (member) => {
    if (!selectedEvent) return;

    try {
      const { error } = await supabase
        .from('event_attendance')
        .delete()
        .eq('event_id', selectedEvent.id)
        .eq('member_id', member.id);

      if (error) throw error;

      // Refresh RSVP members
      await loadRSVPMembers(selectedEvent.id);
      
      // Refresh suggested members to include newly checked-out member
      if (selectedEvent.is_recurring && members.length > 0) {
        await loadSuggestedMembers(selectedEvent, members);
      }
      
      toast({
        title: "Removed",
        description: `${formatName(member.firstname, member.lastname)} removed from event`
      });
    } catch (error) {
      console.error('Error checking out member:', error);
      toast({
        title: "Error",
        description: "Failed to remove member",
        variant: "destructive"
      });
    }
  };

  const parseVolunteerRoles = (roles) => {
    if (!roles) return [];

    if (Array.isArray(roles)) {
      return roles
        .map((role) => {
          if (typeof role === 'string') return role.trim();
          if (role && typeof role === 'object') {
            return (role.role || role.name || '').trim();
          }
          return '';
        })
        .filter(Boolean);
    }

    if (typeof roles === 'string') {
      const trimmedRoles = roles.trim();
      if (!trimmedRoles) return [];

      if (
        (trimmedRoles.startsWith('[') && trimmedRoles.endsWith(']')) ||
        (trimmedRoles.startsWith('{') && trimmedRoles.endsWith('}'))
      ) {
        try {
          return parseVolunteerRoles(JSON.parse(trimmedRoles));
        } catch (error) {
          // Not JSON, continue as newline-delimited text.
        }
      }

      return trimmedRoles
        .split('\n')
        .map((role) => role.trim())
        .filter(Boolean);
    }

    return [];
  };

  // Filter events based on search and filters
  const filteredEvents = useMemo(() => {
    let filtered = events;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(event =>
        event.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        event.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        event.location?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Event type filter
    if (eventTypeFilter !== 'all') {
      filtered = filtered.filter(event => event.event_type === eventTypeFilter);
    }

    // Time window filter
    const now = new Date();
    if (timeWindowFilter === 'today') {
      filtered = filtered.filter(event => isSameDay(new Date(event.start_date), now));
    } else if (timeWindowFilter === 'week') {
      const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
      const endOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() + 6);
      filtered = filtered.filter(event => {
        const eventDate = new Date(event.start_date);
        return eventDate >= startOfWeek && eventDate <= endOfWeek;
      });
    } else if (timeWindowFilter === 'month') {
      filtered = filtered.filter(event => {
        const eventDate = new Date(event.start_date);
        return eventDate.getMonth() === now.getMonth() && eventDate.getFullYear() === now.getFullYear();
      });
    } else if (timeWindowFilter === 'past') {
      filtered = filtered.filter(event => {
        const eventDate = new Date(event.start_date);
        return eventDate < now;
      });
    }

    // Volunteer filter
    if (volunteerFilter === 'needs_volunteers') {
      filtered = filtered.filter(event => event.needs_volunteers === true);
    } else if (volunteerFilter === 'no_volunteers') {
      filtered = filtered.filter(event => event.needs_volunteers !== true);
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      if (sortField === 'start_date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [events, searchQuery, eventTypeFilter, timeWindowFilter, volunteerFilter, sortField, sortDirection]);

  // Get unique event types for filter
  const eventTypeOptions = useMemo(() => {
    const types = new Set();
    events.forEach(event => {
      if (event.event_type) {
        types.add(event.event_type);
      }
    });
    return Array.from(types).sort();
  }, [events]);

  // Pull to refresh functionality
  const handleTouchStart = useCallback((e) => {
    if (window.scrollY === 0) {
      setPullToRefreshY(e.touches[0].clientY);
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (window.scrollY === 0 && e.touches[0].clientY > pullToRefreshY) {
      const deltaY = e.touches[0].clientY - pullToRefreshY;
      if (deltaY > 0) {
        setIsPulling(true);
        setPullToRefreshY(e.touches[0].clientY);
      }
    }
  }, [pullToRefreshY]);

  const handleTouchEnd = useCallback(async () => {
    if (isPulling && window.scrollY === 0) {
      setRefreshing(true);
      await loadEvents();
      setRefreshing(false);
    }
    setIsPulling(false);
    setPullToRefreshY(0);
  }, [isPulling, loadEvents]);

  useEffect(() => {
    loadEvents();
    loadLocations();
    loadGroups();
  }, [loadEvents, loadLocations, loadGroups]);

  // Load members when check-in dialog opens
  useEffect(() => {
    if (isCheckInDialogOpen && selectedEvent) {
      loadMembers();
      loadRSVPMembers(selectedEvent.id);
    }
  }, [isCheckInDialogOpen, selectedEvent, loadMembers, loadRSVPMembers]);

  // Load suggested members after members are loaded
  useEffect(() => {
    if (isCheckInDialogOpen && selectedEvent && selectedEvent.is_recurring && members.length > 0) {
      loadSuggestedMembers(selectedEvent, members);
    }
  }, [isCheckInDialogOpen, selectedEvent, members, loadSuggestedMembers]);

  // Add touch event listeners
  useEffect(() => {
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Initialize mobile scrolling fixes
  useEffect(() => {
    initializeMobileScrolling();
    // Initialize testing in development
    if (process.env.NODE_ENV === 'development') {
      initializeMobileScrollTesting();
    }
  }, []);

  const handleAddEvent = async (eventData) => {
    try {
      await addEvent(eventData);
      toast({
        title: "Success",
        description: "Event created successfully",
      });
      setIsAddDialogOpen(false);
      setFormData({
        title: '',
        description: '',
        date: '',
        time: '',
        location: '',
        event_type: 'Other',
        track_attendance: false,
        recurring: false
      });
      loadEvents();
    } catch (error) {
      console.error('Error adding event:', error);
      toast({
        title: "Error",
        description: "Failed to create event",
        variant: "destructive"
      });
    }
  };

  // Form handlers
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Load event with reminders (similar to desktop)
  const loadEventWithReminders = async (event) => {
    try {
      // For now, just return the event as-is
      // In the future, this could load associated reminders
      return event;
    } catch (error) {
      console.error('Error loading event with reminders:', error);
      return event;
    }
  };

  // Handle edit click
  const handleEditClick = async (event) => {
    console.log('handleEditClick called with event:', event);
    
    // If we're on individual event page, navigate back to events list first
    if (isIndividualEvent) {
      navigate('/events');
      // Small delay to ensure navigation completes
      setTimeout(() => {
        openEditDialog(event);
      }, 100);
    } else {
      openEditDialog(event);
    }
  };

  // Open edit dialog helper function
  const openEditDialog = async (event) => {
    // Check if this is a recurring event
    const isRecurringEvent = event.is_recurring || 
      (event.id && (event.id.includes('_') || (event.id.includes('-') && event.id.length > 36)));
    
    const eventsWithSameTitle = events.filter(e => e.title === event.title);
    const isInstance = event.parent_event_id && !event.is_master;
    const isMaster = event.is_master === true;
    
    const shouldShowEditChoice = (isRecurringEvent || eventsWithSameTitle.length > 1) && 
                                (isInstance || isMaster);
    
    if (shouldShowEditChoice) {
      // Find master event for edit choice dialog
      const masterEvent = events.find(e => 
        e.title === event.title && 
        e.is_master === true && 
        e.is_recurring === true
      ) || events.find(e => 
        e.title === event.title && 
        e.is_recurring === true &&
        !e.parent_event_id
      );
      
      if (masterEvent) {
        setEventToEdit(event);
        setMasterEventToEdit(masterEvent);
        setShowEditChoiceDialog(true);
      } else {
        // No master found, edit this instance
        const eventWithReminders = await loadEventWithReminders(event);
        setEditingEvent(eventWithReminders);
        populateFormForEdit(eventWithReminders);
        setIsEditDialogOpen(true);
      }
    } else {
      // Single event - edit normally
      const eventWithReminders = await loadEventWithReminders(event);
      setEditingEvent(eventWithReminders);
      populateFormForEdit(eventWithReminders);
      setIsEditDialogOpen(true);
    }
  };

  // Populate form with event data for editing
  const populateFormForEdit = (event) => {
    const startDate = new Date(event.start_date);
    const endDate = new Date(event.end_date);
    
    setFormData({
      title: event.title || '',
      description: event.description || '',
      date: startDate.toISOString().split('T')[0],
      time: startDate.toTimeString().slice(0, 5),
      startDate: startDate.toISOString().split('T')[0],
      startTime: startDate.toTimeString().slice(0, 5),
      endDate: endDate.toISOString().split('T')[0],
      endTime: endDate.toTimeString().slice(0, 5),
      location: event.location || '',
      location_id: event.location_id || '',
      event_type: event.event_type || 'Other',
      track_attendance: event.allow_rsvp || false,
      allow_rsvp: event.allow_rsvp || false,
      attendance_type: event.attendance_type || 'rsvp',
      recurring: event.is_recurring || false,
      recurrence_pattern: event.recurrence_pattern || 'none',
      monthly_week: event.monthly_week || '',
      monthly_weekday: event.monthly_weekday || '',
      needs_volunteers: event.needs_volunteers || false,
      volunteer_roles: Array.isArray(event.volunteer_roles) 
        ? event.volunteer_roles.join('\\n') 
        : (event.volunteer_roles || ''),
      enable_reminders: false, // Will be populated if reminders exist
      reminder_type: 'sms',
      reminder_timing_value: 24,
      reminder_timing_unit: 'hours',
      reminder_target: 'all',
      reminder_target_groups: []
    });
  };

  // Handle edit event submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingEvent) return;
    
    // Use same validation and data preparation as create
    const startDate = formData.startDate || formData.date;
    const startTime = formData.startTime || formData.time;
    const endDate = formData.endDate || formData.startDate || formData.date;
    const endTime = formData.endTime || '10:00';
    
    if (!startDate || !startTime) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }
    
    const startDateTime = `${startDate}T${startTime}:00`;
    const endDateTime = `${endDate}T${endTime}:00`;
    
    const eventData = {
      title: formData.title,
      description: formData.description,
      start_date: startDateTime,
      end_date: endDateTime,
      location: formData.location,
      location_id: formData.location_id || null,
      event_type: formData.event_type,
      is_recurring: formData.recurrence_pattern !== 'none',
      recurrence_pattern: formData.recurrence_pattern === 'none' ? null : formData.recurrence_pattern,
      monthly_week: formData.monthly_week || null,
      monthly_weekday: formData.monthly_weekday || null,
      allow_rsvp: formData.allow_rsvp || formData.track_attendance,
      attendance_type: (formData.allow_rsvp || formData.track_attendance) ? (formData.attendance_type || 'rsvp') : null,
      needs_volunteers: formData.needs_volunteers || false,
      volunteer_roles: formData.needs_volunteers && formData.volunteer_roles ? 
        formData.volunteer_roles.split('\\n').filter(role => role.trim() !== '') : null
    };
    
    // Add reminder data if enabled
    if (formData.enable_reminders) {
      eventData.reminders = [{
        reminder_type: formData.reminder_type || 'sms',
        timing_unit: formData.reminder_timing_unit || 'hours',
        timing_value: formData.reminder_timing_value || 24,
        target_type: formData.reminder_target || 'all',
        target_groups: formData.reminder_target === 'groups' ? formData.reminder_target_groups : [],
        message_template: `Reminder: {event_title} on {event_date} at {event_time}. {event_location}`,
        is_enabled: true
      }];
    }
    
    try {
      await updateEvent(editingEvent.id, eventData);
      toast({
        title: "Success",
        description: "Event updated successfully",
      });
      setIsEditDialogOpen(false);
      setEditingEvent(null);
      loadEvents();
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        date: '',
        time: '',
        startDate: '',
        startTime: '09:00',
        endDate: '',
        endTime: '10:00',
        location: '',
        location_id: '',
        event_type: 'Other',
        track_attendance: false,
        allow_rsvp: false,
        attendance_type: 'check-in',
        recurring: false,
        recurrence_pattern: 'none',
        monthly_week: '',
        monthly_weekday: '',
        needs_volunteers: false,
        volunteer_roles: '',
        enable_reminders: false,
        reminder_type: 'sms',
        reminder_timing_value: 24,
        reminder_timing_unit: 'hours',
        reminder_target: 'all',
        reminder_target_groups: []
      });
    } catch (error) {
      console.error('Error updating event:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update event. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Handle delete event
  const handleDeleteEvent = async (eventId) => {
    try {
      await deleteEvent(eventId);
      toast({
        title: "Success",
        description: "Event deleted successfully",
      });
      setIsDeleteDialogOpen(false);
      loadEvents();
      
      // If we're viewing this event individually, go back to events list
      if (isIndividualEvent && selectedEvent?.id === eventId) {
        navigate('/events');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete event. Please try again.",
        variant: "destructive"
      });
    }
  };

  const openVolunteerDialog = (event) => {
    setVolunteerDialogEvent(event);
    setVolunteerRefreshKey(prev => prev + 1);
    setIsVolunteerDialogOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Use new fields if available, fallback to old fields for backward compatibility
    const startDate = formData.startDate || formData.date;
    const startTime = formData.startTime || formData.time;
    const endDate = formData.endDate || formData.startDate || formData.date;
    const endTime = formData.endTime || '10:00';
    
    if (!startDate || !startTime) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }
    
    const startDateTime = `${startDate}T${startTime}:00`;
    const endDateTime = `${endDate}T${endTime}:00`;
    
    const eventData = {
      title: formData.title,
      description: formData.description,
      start_date: startDateTime,
      end_date: endDateTime,
      location: formData.location,
      location_id: formData.location_id || null,
      event_type: formData.event_type,
      is_recurring: formData.recurrence_pattern !== 'none',
      recurrence_pattern: formData.recurrence_pattern === 'none' ? null : formData.recurrence_pattern,
      monthly_week: formData.monthly_week || null,
      monthly_weekday: formData.monthly_weekday || null,
      allow_rsvp: formData.allow_rsvp || formData.track_attendance,
      attendance_type: (formData.allow_rsvp || formData.track_attendance) ? (formData.attendance_type || 'rsvp') : null,
      needs_volunteers: formData.needs_volunteers || false,
      volunteer_roles: formData.needs_volunteers && formData.volunteer_roles ? 
        formData.volunteer_roles.split('\\n').filter(role => role.trim() !== '') : null
    };
    
    // Add reminder data if enabled
    if (formData.enable_reminders) {
      eventData.reminders = [{
        reminder_type: formData.reminder_type || 'sms',
        timing_unit: formData.reminder_timing_unit || 'hours',
        timing_value: formData.reminder_timing_value || 24,
        target_type: formData.reminder_target || 'all',
        target_groups: formData.reminder_target === 'groups' ? formData.reminder_target_groups : [],
        message_template: `Reminder: {event_title} on {event_date} at {event_time}. {event_location}`,
        is_enabled: true
      }];
    }
    
    handleAddEvent(eventData);
  };

  const handleUpdateEvent = async (eventData) => {
    try {
      await updateEvent(selectedEvent.id, eventData);
      toast({
        title: "Success",
        description: "Event updated successfully",
      });
      setIsEditDialogOpen(false);
      setSelectedEvent(null);
      loadEvents();
    } catch (error) {
      console.error('Error updating event:', error);
      toast({
        title: "Error",
        description: "Failed to update event",
        variant: "destructive"
      });
    }
  };


  const getEventStatusColor = (event) => {
    const now = new Date();
    const eventDate = new Date(event.start_date);
    
    if (isSameDay(eventDate, now)) return 'bg-green-500';
    if (isAfter(eventDate, now)) return 'bg-blue-500';
    return 'bg-gray-500 dark:bg-gray-600';
  };

  const getEventStatusIcon = (event) => {
    const now = new Date();
    const eventDate = new Date(event.start_date);
    
    if (isSameDay(eventDate, now)) return CheckCircle;
    if (isAfter(eventDate, now)) return Clock;
    return XCircle;
  };

  const getEventTypeIcon = (eventType) => {
    const icons = {
      'service': Church,
      'meeting': Users,
      'social': Heart,
      'outreach': Gift,
      'youth': Baby,
      'music': Music,
      'default': Calendar
    };
    return icons[eventType?.toLowerCase()] || icons.default;
  };

  const getEventTypeColor = (eventType) => {
    const colors = {
      'service': 'text-blue-500',
      'meeting': 'text-purple-500',
      'social': 'text-pink-500',
      'outreach': 'text-green-500',
      'youth': 'text-orange-500',
      'music': 'text-yellow-500',
      'default': 'text-gray-500 dark:text-gray-400'
    };
    return colors[eventType?.toLowerCase()] || colors.default;
  };

  // Calendar functionality
  const daysInMonth = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  const eventsByDate = useMemo(() => {
    const eventsMap = {};
    events.forEach(event => {
      const eventDate = new Date(event.start_date);
      const eventDateKey = format(eventDate, 'yyyy-MM-dd');
      if (!eventsMap[eventDateKey]) {
        eventsMap[eventDateKey] = [];
      }
      eventsMap[eventDateKey].push(event);
    });
    return eventsMap;
  }, [events]);

  // Filter members for search in check-in dialog
  const filteredMembers = members.filter(member =>
    `${member.firstname} ${member.lastname}`.toLowerCase().includes(memberSearchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <div className="space-y-2">
          <div className="h-8 bg-muted rounded w-32 animate-pulse" />
          <div className="h-4 bg-muted rounded w-48 animate-pulse" />
        </div>
        <div className="grid grid-cols-1 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-24 bg-muted rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // If viewing individual event, show event details
  if (isIndividualEvent) {
    if (!selectedEvent) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Card>
            <CardContent className="p-8 text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <h3 className="text-lg font-medium text-foreground mb-2">Event Not Found</h3>
              <p className="text-muted-foreground mb-4">The requested event could not be found.</p>
              <Button onClick={() => navigate('/events')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Events
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    const TypeIcon = getEventTypeIcon(selectedEvent.event_type);
    const isToday = isSameDay(new Date(selectedEvent.start_date), new Date());
    const isUpcoming = isAfter(new Date(selectedEvent.start_date), new Date());
    const selectedEventVolunteerRoles = parseVolunteerRoles(selectedEvent.volunteer_roles);

    return (
      <div className="min-h-screen bg-background">
        <div className="p-4 space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/events')}
              className="p-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Events
            </Button>
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Event Actions</DropdownMenuLabel>
                  <PermissionGuard permission={PERMISSIONS.EVENTS_EDIT}>
                    <DropdownMenuItem onClick={() => handleEditClick(selectedEvent)}>
                      <Edit className="mr-2 h-4 w-4" />
                      Edit Event
                    </DropdownMenuItem>
                  </PermissionGuard>
                  <PermissionGuard permission={PERMISSIONS.EVENTS_DELETE}>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => setIsDeleteDialogOpen(true)}
                      className="text-red-600 focus:text-red-600"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Event
                    </DropdownMenuItem>
                  </PermissionGuard>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Event Details */}
          <Card>
            <CardContent className="p-6">
              <div className="space-y-6">
                {/* Event Header */}
                <div className="flex items-start space-x-4">
                  <div className={`w-16 h-16 rounded-lg flex items-center justify-center ${
                    isToday ? 'bg-green-100' : 'bg-blue-100'
                  }`}>
                    <TypeIcon className={`h-8 w-8 ${
                      isToday ? 'text-green-600' : getEventTypeColor(selectedEvent.event_type)
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h1 className="text-2xl font-bold text-foreground mb-2">
                      {selectedEvent.title}
                    </h1>
                    <div className="flex flex-wrap gap-2">
                      {isToday && (
                        <Badge className="bg-green-100 text-green-800">Today</Badge>
                      )}
                      {selectedEvent.event_type && (
                        <Badge variant="outline">{selectedEvent.event_type}</Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Event Info */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-foreground">
                        {format(new Date(selectedEvent.start_date), 'EEEE, MMMM d, yyyy')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(selectedEvent.start_date), 'h:mm a')}
                        {selectedEvent.end_date && ` - ${format(new Date(selectedEvent.end_date), 'h:mm a')}`}
                      </p>
                    </div>
                  </div>

                  {selectedEvent.location && (
                    <div className="flex items-center gap-3">
                      <MapPin className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-foreground">{selectedEvent.location}</p>
                      </div>
                    </div>
                  )}

                  {selectedEvent.attendance > 0 && (
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-foreground">
                          {selectedEvent.attendance} attending
                        </p>
                      </div>
                    </div>
                  )}

                  {selectedEvent.needs_volunteers && (
                    <div className="flex items-start gap-3">
                      <UserPlus className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">Volunteers needed</p>
                        {selectedEventVolunteerRoles.length > 0 && (
                          <p className="text-sm text-muted-foreground">
                            Roles: {selectedEventVolunteerRoles.join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Description */}
                {selectedEvent.description && (
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-foreground">Description</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {selectedEvent.description}
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col gap-3 pt-4">
                  {/* Quick Check-In Button - Available for all events */}
                  <Button 
                    className={`w-full ${
                      isToday 
                        ? 'bg-green-600 hover:bg-green-700' 
                        : isUpcoming 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-orange-600 hover:bg-orange-700'
                    }`}
                    onClick={() => setIsCheckInDialogOpen(true)}
                  >
                    <UserCheck className="h-4 w-4 mr-2" />
                    {isToday 
                      ? 'Quick Check-In' 
                      : isUpcoming 
                      ? 'Quick Check-In' 
                      : 'Add Attendance (Past Event)'
                    }
                  </Button>

                  {selectedEvent.needs_volunteers && (
                    <Button
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => openVolunteerDialog(selectedEvent)}
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add Volunteers
                    </Button>
                  )}
                  
                  {/* Kiosk Button - Only for current/upcoming events */}
                  {(isToday || (isUpcoming && isAfter(new Date(selectedEvent.start_date), addHours(new Date(), -2)))) && (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => navigate('/kiosk')}
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Open Kiosk
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Check-in Dialog */}
        <Dialog open={isCheckInDialogOpen} onOpenChange={setIsCheckInDialogOpen}>
          <DialogContent className="w-[100vw] h-[100vh] max-w-none p-0 m-0 [&>button]:hidden mobile-dialog flex flex-col">
            <DialogHeader className="sr-only">
              <DialogTitle>{selectedEvent?.title} - Check In</DialogTitle>
              <DialogDescription>
                {alreadyRSVPMembers.length} people checked in
              </DialogDescription>
            </DialogHeader>
            
            {/* Custom Mobile Header */}
            <div className="flex items-center justify-between p-4 border-b bg-white dark:bg-gray-800">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-semibold truncate">{selectedEvent?.title}</h2>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400">{alreadyRSVPMembers.length} checked in</p>
                  {selectedEvent && !isSameDay(new Date(selectedEvent.start_date), new Date()) && !isAfter(new Date(selectedEvent.start_date), new Date()) && (
                    <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 px-2 py-1 rounded-full">
                      Past Event
                    </span>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsCheckInDialogOpen(false)}
                className="ml-2 flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Content - Force absolute positioning */}
            <div className="absolute top-20 left-0 right-0 bottom-0 flex flex-col">
              <Tabs defaultValue="available" className="flex-1 flex flex-col h-full">
                <TabsList className="grid w-full grid-cols-2 mx-4 mt-2 h-8 flex-shrink-0">
                  <TabsTrigger value="available" className="text-xs">Available People</TabsTrigger>
                  <TabsTrigger value="checkedin" className="text-xs">Checked In ({alreadyRSVPMembers.length})</TabsTrigger>
                </TabsList>
                
                <TabsContent value="available" className="flex-1 flex flex-col mt-2 mx-4 min-h-0 data-[state=active]:flex">
                  {/* Search */}
                  <Input
                    placeholder="Search members..."
                    value={memberSearchQuery}
                    onChange={(e) => setMemberSearchQuery(e.target.value)}
                    className="mb-3 h-9 flex-shrink-0"
                  />
                  
                  {/* Members List - Absolute positioned for full height */}
                  <div className="absolute top-20 left-4 right-4 bottom-4 overflow-y-auto space-y-2" style={getMobileScrollStyles()}>
                    {/* Suggested Members */}
                    <br/>
                    {suggestedMembers.filter(member => !alreadyRSVPMembers.some(rsvp => rsvp.id === member.id)).length > 0 && (
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Frequent Attendees</div>
                        {suggestedMembers
                          .filter(member => !alreadyRSVPMembers.some(rsvp => rsvp.id === member.id))
                          .map(member => (
                          <Button
                            key={member.id}
                            variant="outline"
                            className="w-full h-16 p-3 justify-start"
                            onClick={() => handleCheckIn(member)}
                          >
                            <Avatar className="h-10 w-10 mr-3">
                              <AvatarImage src={member.image_url} alt={formatName(member.firstname, member.lastname)} />
                              <AvatarFallback>{getInitials(member.firstname, member.lastname)}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 text-left">
                              <div className="font-medium">{formatName(member.firstname, member.lastname)}</div>
                              <div className="text-xs text-blue-600">⭐ Frequent attendee</div>
                            </div>
                          </Button>
                        ))}
                      </div>
                    )}
                    
                    {/* All Members */}
                    <div>
                      {suggestedMembers.filter(member => !alreadyRSVPMembers.some(rsvp => rsvp.id === member.id)).length > 0 && (
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">All Members</div>
                      )}
                      {filteredMembers
                        .filter(member => !alreadyRSVPMembers.some(rsvp => rsvp.id === member.id))
                        .map(member => (
                          <Button
                            key={member.id}
                            variant="outline"
                            className="w-full h-16 p-3 justify-start"
                            onClick={() => handleCheckIn(member)}
                          >
                            <Avatar className="h-10 w-10 mr-3">
                              <AvatarImage src={member.image_url} alt={formatName(member.firstname, member.lastname)} />
                              <AvatarFallback>{getInitials(member.firstname, member.lastname)}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 text-left">
                              <div className="font-medium">{formatName(member.firstname, member.lastname)}</div>
                            </div>
                          </Button>
                        ))}
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="checkedin" className="flex-1 flex flex-col mt-2 mx-4 min-h-0 data-[state=active]:flex">
                  {/* Checked-in Members List - Absolute positioned for full height */}
                  <div className="absolute top-20 left-4 right-4 bottom-4 overflow-y-auto space-y-2" style={getMobileScrollStyles()}>
                    {alreadyRSVPMembers.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No one checked in yet</p>
                      </div>
                    ) : (
                      <>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 px-1">
                          Tap to remove someone from the event
                        </div>
                        {alreadyRSVPMembers.map(member => (
                          <Button
                            key={member.id}
                            variant="outline"
                            className="w-full h-16 p-3 justify-start bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-200 dark:hover:border-red-800 transition-colors"
                            onClick={() => handleCheckOut(member)}
                          >
                            <Avatar className="h-10 w-10 mr-3">
                              <AvatarImage src={member.image_url} alt={formatName(member.firstname, member.lastname)} />
                              <AvatarFallback>{getInitials(member.firstname, member.lastname)}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 text-left">
                              <div className="font-medium">{formatName(member.firstname, member.lastname)}</div>
                              <div className="text-xs text-green-600">✓ Checked in</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Check className="h-4 w-4 text-green-600" />
                              <X className="h-4 w-4 text-gray-400 group-hover:text-red-500" />
                            </div>
                          </Button>
                        ))}
                      </>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </DialogContent>
        </Dialog>

        {/* Volunteer Management Dialog */}
        <Dialog
          open={isVolunteerDialogOpen}
          onOpenChange={(open) => {
            setIsVolunteerDialogOpen(open);
            if (!open) {
              setVolunteerDialogEvent(null);
            }
          }}
        >
          <DialogContent className="w-[100vw] h-[100vh] max-w-none p-0 m-0 [&>button]:hidden mobile-dialog flex flex-col">
            <DialogHeader className="sr-only">
              <DialogTitle>Volunteer Management</DialogTitle>
              <DialogDescription>
                Add and manage volunteers for {volunteerDialogEvent?.title || selectedEvent?.title}
              </DialogDescription>
            </DialogHeader>

            {/* Custom Mobile Header */}
            <div className="flex items-center justify-between p-4 border-b bg-white dark:bg-gray-800">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-semibold truncate">
                  {volunteerDialogEvent?.title || selectedEvent?.title}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Volunteer Management</p>
                {selectedEventVolunteerRoles.length > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                    Roles: {selectedEventVolunteerRoles.join(', ')}
                  </p>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsVolunteerDialogOpen(false)}
                className="ml-2 flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="absolute top-20 left-0 right-0 bottom-0 flex flex-col">
              <Tabs defaultValue="current" className="flex-1 flex flex-col h-full">
                <TabsList className="grid w-full grid-cols-2 mx-4 mt-2 h-8 flex-shrink-0">
                  <TabsTrigger value="current" className="text-xs">Current Volunteers</TabsTrigger>
                  <TabsTrigger value="add" className="text-xs">Add Volunteer</TabsTrigger>
                </TabsList>

                <TabsContent value="current" className="flex-1 flex flex-col mt-2 mx-4 min-h-0 data-[state=active]:flex">
                  <div className="absolute top-12 left-4 right-4 bottom-4 overflow-y-auto space-y-2" style={getMobileScrollStyles()}>
                    <VolunteerList
                      key={`volunteer-list-${volunteerRefreshKey}-${volunteerDialogEvent?.id || selectedEvent?.id}`}
                      eventId={volunteerDialogEvent?.id || selectedEvent?.id}
                      availableRoles={selectedEventVolunteerRoles}
                      onVolunteerUpdated={() => setVolunteerRefreshKey(prev => prev + 1)}
                      onVolunteerRemoved={() => setVolunteerRefreshKey(prev => prev + 1)}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="add" className="flex-1 flex flex-col mt-2 mx-4 min-h-0 data-[state=active]:flex">
                  <div className="absolute top-12 left-4 right-4 bottom-4 overflow-y-auto space-y-2 pb-6" style={getMobileScrollStyles()}>
                    <AddVolunteerForm
                      key={`add-volunteer-form-${volunteerRefreshKey}-${volunteerDialogEvent?.id || selectedEvent?.id}`}
                      eventId={volunteerDialogEvent?.id || selectedEvent?.id}
                      availableRoles={selectedEventVolunteerRoles}
                      onVolunteerAdded={() => setVolunteerRefreshKey(prev => prev + 1)}
                    />
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create Event Dialog */}
        <CreateEventDialog 
          isOpen={isAddDialogOpen}
          onClose={() => setIsAddDialogOpen(false)}
          onSubmit={handleSubmit}
          formData={formData}
          onInputChange={handleInputChange}
          eventTypes={eventTypes}
          isEditing={false}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Pull to refresh indicator */}
      <AnimatePresence>
        {refreshing && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-16 left-0 right-0 z-50 bg-primary text-primary-foreground py-2 text-center"
          >
            <RefreshCw className="h-4 w-4 animate-spin inline mr-2" />
            Refreshing...
          </motion.div>
        )}
      </AnimatePresence>

      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Events</h1>
            <p className="text-muted-foreground">
              {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4" />
            </Button>
            <PermissionButton 
              permission={PERMISSIONS.EVENTS_CREATE}
              size="sm"
              onClick={() => setIsAddDialogOpen(true)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </PermissionButton>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-3 p-4 bg-muted/50 rounded-lg"
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select
                    value={eventTypeFilter}
                    onValueChange={setEventTypeFilter}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {eventTypeOptions.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Period</Label>
                  <Select
                    value={timeWindowFilter}
                    onValueChange={setTimeWindowFilter}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Time</SelectItem>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="week">This Week</SelectItem>
                      <SelectItem value="month">This Month</SelectItem>
                      <SelectItem value="past">Past Events</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Volunteers</Label>
                  <Select
                    value={volunteerFilter}
                    onValueChange={setVolunteerFilter}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Events</SelectItem>
                      <SelectItem value="needs_volunteers">Needs Volunteers</SelectItem>
                      <SelectItem value="no_volunteers">No Volunteers Needed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Sort By</Label>
                <Select
                  value={sortField}
                  onValueChange={setSortField}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="start_date">Date</SelectItem>
                    <SelectItem value="title">Title</SelectItem>
                    <SelectItem value="event_type">Type</SelectItem>
                    <SelectItem value="location">Location</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
            <TabsTrigger value="today">Today</TabsTrigger>
            <TabsTrigger value="calendar">Calendar</TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="space-y-4">
            {/* Upcoming Events List */}
            <div className="space-y-3">
              {filteredEvents.filter(event => isAfter(new Date(event.start_date), new Date())).map((event) => {
              const StatusIcon = getEventStatusIcon(event);
              const TypeIcon = getEventTypeIcon(event.event_type);
              const isToday = isSameDay(new Date(event.start_date), new Date());
              const isUpcoming = isAfter(new Date(event.start_date), new Date());
              const volunteerRoles = parseVolunteerRoles(event.volunteer_roles);
              
              return (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-3">
                        <div className="relative">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            isToday ? 'bg-green-100' : 'bg-blue-100'
                          }`}>
                            <TypeIcon className={`h-6 w-6 ${
                              isToday ? 'text-green-600' : getEventTypeColor(event.event_type)
                            }`} />
                          </div>
                          <div className={`absolute -bottom-1 -right-1 p-1 rounded-full ${getEventStatusColor(event)}`}>
                            <StatusIcon className="h-3 w-3 text-white" />
                          </div>
                        </div>
                        
                        <div 
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => navigate(`/events/${event.id}`)}
                        >
                          <div className="flex items-start justify-between">
                            <h3 className="font-medium text-sm text-foreground truncate">
                              {event.title}
                            </h3>
                            {isToday && (
                              <Badge variant="secondary" className="ml-2 text-xs bg-green-100 text-green-800">
                                Today
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                            <div className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              <span>{format(new Date(event.start_date), 'MMM d, h:mm a')}</span>
                            </div>
                            {event.location && (
                              <div className="flex items-center gap-1">
                                <MapPin className="h-4 w-4" />
                                <span className="truncate">{event.location}</span>
                              </div>
                            )}
                          </div>
                          {event.attendance > 0 && (
                            <div className="flex items-center gap-1 text-sm text-green-600 mt-2">
                              <Users className="h-4 w-4" />
                              <span>{event.attendance} attending</span>
                            </div>
                          )}
                          {event.needs_volunteers && (
                            <div className="mt-2">
                              <div className="flex items-center gap-1 text-sm text-amber-700 dark:text-amber-400">
                                <UserPlus className="h-4 w-4" />
                                <span>
                                  Needs volunteers
                                  {volunteerRoles.length > 0
                                    ? ` (${volunteerRoles.length} role${volunteerRoles.length === 1 ? '' : 's'})`
                                    : ''}
                                </span>
                              </div>
                              {volunteerRoles.length > 0 && (
                                <p className="text-xs text-muted-foreground mt-1 truncate">
                                  Roles: {volunteerRoles.slice(0, 2).join(', ')}
                                  {volunteerRoles.length > 2 ? ` +${volunteerRoles.length - 2} more` : ''}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Event Actions</DropdownMenuLabel>
                              <DropdownMenuItem onClick={() => navigate(`/events/${event.id}`)}>
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              <PermissionGuard permission={PERMISSIONS.EVENTS_EDIT}>
                                <DropdownMenuItem onClick={() => handleEditClick(event)}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit Event
                                </DropdownMenuItem>
                              </PermissionGuard>
                              <PermissionGuard permission={PERMISSIONS.EVENTS_DELETE}>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => {
                                    setSelectedEvent(event);
                                    setIsDeleteDialogOpen(true);
                                  }}
                                  className="text-red-600 focus:text-red-600"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete Event
                                </DropdownMenuItem>
                              </PermissionGuard>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
              })}
            </div>
            
            {/* Empty State for Upcoming */}
            {filteredEvents.filter(event => isAfter(new Date(event.start_date), new Date())).length === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No upcoming events</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="today" className="space-y-4">
            {/* Today's Events List */}
            <div className="space-y-3">
              {filteredEvents.filter(event => isSameDay(new Date(event.start_date), new Date())).map((event) => {
                const StatusIcon = getEventStatusIcon(event);
                const TypeIcon = getEventTypeIcon(event.event_type);
                const isToday = true;
                const volunteerRoles = parseVolunteerRoles(event.volunteer_roles);
                
                return (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Card 
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => navigate(`/events/${event.id}`)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center space-x-3">
                          <div className="relative">
                            <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-green-100">
                              <TypeIcon className="h-6 w-6 text-green-600" />
                            </div>
                            <div className="absolute -bottom-1 -right-1 p-1 rounded-full bg-green-500">
                              <StatusIcon className="h-3 w-3 text-white" />
                            </div>
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <h3 className="font-medium text-sm text-foreground truncate">
                                {event.title}
                              </h3>
                              <Badge variant="secondary" className="ml-2 text-xs bg-green-100 text-green-800">
                                Today
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                              <div className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                <span>{format(new Date(event.start_date), 'h:mm a')}</span>
                              </div>
                              {event.location && (
                                <div className="flex items-center gap-1">
                                  <MapPin className="h-4 w-4" />
                                  <span className="truncate">{event.location}</span>
                                </div>
                              )}
                            </div>
                            {event.attendance > 0 && (
                              <div className="flex items-center gap-1 text-sm text-green-600 mt-2">
                                <Users className="h-4 w-4" />
                                <span>{event.attendance} attending</span>
                              </div>
                            )}
                            {event.needs_volunteers && (
                              <div className="mt-2">
                                <div className="flex items-center gap-1 text-sm text-amber-700 dark:text-amber-400">
                                  <UserPlus className="h-4 w-4" />
                                  <span>
                                    Needs volunteers
                                    {volunteerRoles.length > 0
                                      ? ` (${volunteerRoles.length} role${volunteerRoles.length === 1 ? '' : 's'})`
                                      : ''}
                                  </span>
                                </div>
                                {volunteerRoles.length > 0 && (
                                  <p className="text-xs text-muted-foreground mt-1 truncate">
                                    Roles: {volunteerRoles.slice(0, 2).join(', ')}
                                    {volunteerRoles.length > 2 ? ` +${volunteerRoles.length - 2} more` : ''}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                          <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
            
            {/* Empty State for Today */}
            {filteredEvents.filter(event => isSameDay(new Date(event.start_date), new Date())).length === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No events today</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="calendar" className="space-y-4">
            {/* Calendar View */}
          <div className="space-y-4">
            {/* Month Navigation */}
            <div className="flex items-center justify-between p-3 bg-card rounded-lg">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <h2 className="text-lg font-semibold text-foreground">
                {format(currentMonth, 'MMMM yyyy')}
              </h2>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                className="h-8 w-8 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Calendar Grid */}
            <Card>
              <CardContent className="p-4">
                {/* Day Headers */}
                <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-muted-foreground mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="p-2">{day}</div>
                  ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-1">
                  {/* Empty cells for days before month starts */}
                  {Array.from({ length: startOfMonth(currentMonth).getDay() }, (_, i) => (
                    <div key={`empty-${i}`} className="h-10"></div>
                  ))}

                  {/* Days of the month */}
                  {daysInMonth.map(day => {
                    const dayEvents = eventsByDate[format(day, 'yyyy-MM-dd')] || [];
                    const isToday = isSameDay(day, new Date());

                    return (
                      <div
                        key={format(day, 'yyyy-MM-dd')}
                        className={`h-10 flex items-center justify-center text-sm rounded-lg relative cursor-pointer ${
                          isToday
                            ? 'bg-primary text-primary-foreground font-bold'
                            : dayEvents.length > 0
                            ? 'bg-blue-100 text-blue-900 font-medium hover:bg-blue-200'
                            : 'text-foreground hover:bg-muted'
                        }`}
                        onClick={() => {
                          if (dayEvents.length === 1) {
                            navigate(`/events/${dayEvents[0].id}`);
                          } else if (dayEvents.length > 1) {
                            // Show events for that day
                            toast({
                              title: `Events on ${format(day, 'MMM d')}`,
                              description: `${dayEvents.length} events scheduled`
                            });
                          }
                        }}
                      >
                        {format(day, 'd')}
                        {dayEvents.length > 0 && (
                          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-current rounded-full"></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Today's Events */}
            {(() => {
              const todayEvents = events.filter(event =>
                isSameDay(new Date(event.start_date), new Date())
              );

              if (todayEvents.length === 0) return null;

              return (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Today's Events</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {todayEvents.map(event => (
                      <div
                        key={event.id}
                        onClick={() => navigate(`/events/${event.id}`)}
                        className="flex items-center space-x-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50"
                      >
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <Calendar className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm text-foreground truncate">{event.title}</h4>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(event.start_date), 'h:mm a')}
                            {event.location && ` • ${event.location}`}
                          </p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              );
            })()}
          </div>
          </TabsContent>
        </Tabs>

        {/* Empty State */}
        {filteredEvents.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">
                {searchQuery ? 'No events found matching your search' : 'No events found'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Create Event Dialog */}
      <CreateEventDialog 
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onSubmit={handleSubmit}
        formData={formData}
        onInputChange={handleInputChange}
        eventTypes={eventTypes}
        locations={locations}
        groups={groups}
        isEditing={false}
      />

      {/* Edit Event Dialog */}
      <CreateEventDialog 
        isOpen={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false);
          setEditingEvent(null);
        }}
        onSubmit={handleEditSubmit}
        formData={formData}
        onInputChange={handleInputChange}
        eventTypes={eventTypes}
        locations={locations}
        groups={groups}
        isEditing={true}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="w-[90vw] max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Event</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedEvent?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row gap-2 justify-end">
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => selectedEvent && handleDeleteEvent(selectedEvent.id)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Choice Dialog for Recurring Events */}
      {showEditChoiceDialog && eventToEdit && masterEventToEdit && (
        <Dialog open={showEditChoiceDialog} onOpenChange={setShowEditChoiceDialog}>
          <DialogContent className="w-[90vw] max-w-md p-6">
            <DialogHeader>
              <DialogTitle className="text-xl">What would you like to edit?</DialogTitle>
              <DialogDescription className="text-base mt-2">
                This is a recurring event. You can edit either this specific instance or the master event that controls all future instances.
              </DialogDescription>
            </DialogHeader>
            
            <div className="mt-6 space-y-4">
              {/* Edit Instance Option */}
              <div 
                className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer" 
                onClick={async () => {
                  const eventWithReminders = await loadEventWithReminders(eventToEdit);
                  setEditingEvent(eventWithReminders);
                  populateFormForEdit(eventWithReminders);
                  setIsEditDialogOpen(true);
                  setShowEditChoiceDialog(false);
                  setEventToEdit(null);
                  setMasterEventToEdit(null);
                }}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded-full border-2 border-blue-500 bg-blue-100"></div>
                  <div>
                    <h4 className="font-medium text-gray-900">Edit This Instance</h4>
                    <p className="text-sm text-gray-600">
                      Change details for this specific occurrence only. 
                      Other instances will remain unchanged.
                    </p>
                  </div>
                </div>
              </div>

              {/* Edit Master Event Option */}
              <div 
                className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer" 
                onClick={async () => {
                  const eventWithReminders = await loadEventWithReminders(masterEventToEdit);
                  setEditingEvent(eventWithReminders);
                  populateFormForEdit(eventWithReminders);
                  setIsEditDialogOpen(true);
                  setShowEditChoiceDialog(false);
                  setEventToEdit(null);
                  setMasterEventToEdit(null);
                }}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded-full border-2 border-green-500 bg-green-100"></div>
                  <div>
                    <h4 className="font-medium text-gray-900">Edit Master Event</h4>
                    <p className="text-sm text-gray-600">
                      Change the master event template. This will affect all future instances of this recurring event.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter className="mt-6">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowEditChoiceDialog(false);
                  setEventToEdit(null);
                  setMasterEventToEdit(null);
                }}
              >
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Create/Edit Event Dialog Component
function CreateEventDialog({ isOpen, onClose, onSubmit, formData, onInputChange, eventTypes, locations = [], groups = [], isEditing }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[100vw] h-[100vh] max-w-none p-0 m-0">
        <DialogHeader className="sr-only">
          <DialogTitle>{isEditing ? 'Edit Event' : 'Create New Event'}</DialogTitle>
          <DialogDescription>
            Fill out the form below to {isEditing ? 'update' : 'create'} an event for your organization.
          </DialogDescription>
        </DialogHeader>
        
        {/* Custom Mobile Header */}
        <div className="flex items-center justify-between p-4 border-b bg-white dark:bg-gray-800">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <h2 className="text-lg font-semibold">{isEditing ? 'Edit Event' : 'New Event'}</h2>
          <Button 
            type="submit" 
            form={isEditing ? "edit-event-form" : "create-event-form"}
            size="sm"
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-4">
          <form 
            id={isEditing ? "edit-event-form" : "create-event-form"}
            onSubmit={onSubmit} 
            className="space-y-6"
          >
            {/* Basic Info Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
              
              <div>
                <Label htmlFor={isEditing ? "edit-title" : "title"}>Event Title *</Label>
                <Input
                  id={isEditing ? "edit-title" : "title"}
                  value={formData.title}
                  onChange={(e) => onInputChange('title', e.target.value)}
                  className="h-12 text-base"
                  required
                />
              </div>

              <div>
                <Label htmlFor={isEditing ? "edit-description" : "description"}>Description</Label>
                <Textarea
                  id={isEditing ? "edit-description" : "description"}
                  value={formData.description}
                  onChange={(e) => onInputChange('description', e.target.value)}
                  className="min-h-[80px] text-base"
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor={isEditing ? "edit-event-type" : "event-type"}>Event Type</Label>
                <Select value={formData.event_type} onValueChange={(value) => onInputChange('event_type', value)}>
                  <SelectTrigger className="h-12 text-base">
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent>
                    {eventTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date & Time Section */}
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Date & Time</h3>
              
              <div>
                <Label>Start Date & Time *</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <Input
                    type="date"
                    value={formData.startDate || formData.date}
                    onChange={(e) => onInputChange('startDate', e.target.value)}
                    className="h-12 text-base"
                    required
                  />
                  <Select value={formData.startTime || formData.time} onValueChange={(value) => onInputChange('startTime', value)}>
                    <SelectTrigger className="h-12 text-base">
                      <SelectValue placeholder="Start time" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {Array.from({ length: 48 }, (_, i) => {
                        const hour = Math.floor(i / 2);
                        const minute = i % 2 === 0 ? '00' : '30';
                        const time24 = `${hour.toString().padStart(2, '0')}:${minute}`;
                        const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                        const ampm = hour < 12 ? 'AM' : 'PM';
                        const time12 = `${hour12}:${minute} ${ampm}`;
                        
                        return (
                          <SelectItem key={time24} value={time24}>
                            {time12}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>End Date & Time *</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <Input
                    type="date"
                    value={formData.endDate || formData.startDate || formData.date}
                    onChange={(e) => onInputChange('endDate', e.target.value)}
                    className="h-12 text-base"
                    required
                  />
                  <Select value={formData.endTime || '10:00'} onValueChange={(value) => onInputChange('endTime', value)}>
                    <SelectTrigger className="h-12 text-base">
                      <SelectValue placeholder="End time" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {Array.from({ length: 48 }, (_, i) => {
                        const hour = Math.floor(i / 2);
                        const minute = i % 2 === 0 ? '00' : '30';
                        const time24 = `${hour.toString().padStart(2, '0')}:${minute}`;
                        const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                        const ampm = hour < 12 ? 'AM' : 'PM';
                        const time12 = `${hour12}:${minute} ${ampm}`;
                        
                        return (
                          <SelectItem key={time24} value={time24}>
                            {time12}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Location Section */}
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Location</h3>
              <div>
                <Label htmlFor="location_id">Event Location</Label>
                <Select
                  value={formData.location_id || 'no-location'}
                  onValueChange={(value) => {
                    if (value === 'no-location') {
                      onInputChange('location_id', '');
                      onInputChange('location', '');
                    } else {
                      const selectedLocation = locations.find(l => l.id === value);
                      onInputChange('location_id', value);
                      onInputChange('location', selectedLocation ? selectedLocation.name : '');
                    }
                  }}
                >
                  <SelectTrigger className="h-12 text-base">
                    <SelectValue placeholder="Select a location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-location">No location</SelectItem>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        <div className="flex items-center space-x-2">
                          {location.location_type === 'room' && <Building className="h-4 w-4" />}
                          {location.location_type === 'outdoor' && <MapPin className="h-4 w-4" />}
                          <span>{location.name}</span>
                          {location.capacity && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">({location.capacity} capacity)</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Custom Location Input (fallback) */}
                <div className="mt-2">
                  <Label htmlFor={isEditing ? "edit-location" : "location"}>Custom Location (if not in list)</Label>
                  <Input
                    id={isEditing ? "edit-location" : "location"}
                    value={formData.location}
                    onChange={(e) => onInputChange('location', e.target.value)}
                    className="h-12 text-base"
                    placeholder="Enter custom location if not in the list above"
                  />
                </div>
              </div>
            </div>

            {/* Attendance Section */}
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Attendance</h3>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={isEditing ? "edit-track-attendance" : "track-attendance"}
                  checked={formData.track_attendance || formData.allow_rsvp}
                  onCheckedChange={(checked) => onInputChange('allow_rsvp', checked)}
                />
                <Label htmlFor={isEditing ? "edit-track-attendance" : "track-attendance"}>Track Attendance</Label>
              </div>

              {(formData.track_attendance || formData.allow_rsvp) && (
                <div>
                  <Label>Attendance Type</Label>
                  <Select value={formData.attendance_type || 'check-in'} onValueChange={(value) => onInputChange('attendance_type', value)}>
                    <SelectTrigger className="h-12 text-base">
                      <SelectValue placeholder="Select attendance type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rsvp">RSVP (Members respond in advance)</SelectItem>
                      <SelectItem value="check-in">Check-in (Track attendance at event)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Recurring Events Section */}
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recurring Event</h3>
              
              <div>
                <Label>Recurrence Pattern</Label>
                <Select value={formData.recurrence_pattern || 'none'} onValueChange={(value) => onInputChange('recurrence_pattern', value)}>
                  <SelectTrigger className="h-12 text-base">
                    <SelectValue placeholder="Select recurrence" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Recurrence</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Biweekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="monthly_weekday">Monthly (Specific Weekday)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.recurrence_pattern === 'monthly_weekday' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Week of Month</Label>
                    <Select value={formData.monthly_week || ''} onValueChange={(value) => onInputChange('monthly_week', value)}>
                      <SelectTrigger className="h-12 text-base">
                        <SelectValue placeholder="Select week" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">First</SelectItem>
                        <SelectItem value="2">Second</SelectItem>
                        <SelectItem value="3">Third</SelectItem>
                        <SelectItem value="4">Fourth</SelectItem>
                        <SelectItem value="5">Last</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Day of Week</Label>
                    <Select value={formData.monthly_weekday || ''} onValueChange={(value) => onInputChange('monthly_weekday', value)}>
                      <SelectTrigger className="h-12 text-base">
                        <SelectValue placeholder="Select day" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Sunday</SelectItem>
                        <SelectItem value="1">Monday</SelectItem>
                        <SelectItem value="2">Tuesday</SelectItem>
                        <SelectItem value="3">Wednesday</SelectItem>
                        <SelectItem value="4">Thursday</SelectItem>
                        <SelectItem value="5">Friday</SelectItem>
                        <SelectItem value="6">Saturday</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            {/* Volunteers Section */}
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Volunteers</h3>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={isEditing ? "edit-needs-volunteers" : "needs-volunteers"}
                  checked={formData.needs_volunteers}
                  onCheckedChange={(checked) => onInputChange('needs_volunteers', checked)}
                />
                <Label htmlFor={isEditing ? "edit-needs-volunteers" : "needs-volunteers"}>This event needs volunteers</Label>
              </div>

              {formData.needs_volunteers && (
                <div>
                  <Label htmlFor={isEditing ? "edit-volunteer-roles" : "volunteer-roles"}>Volunteer Roles (one per line)</Label>
                  <Textarea
                    id={isEditing ? "edit-volunteer-roles" : "volunteer-roles"}
                    value={formData.volunteer_roles || ''}
                    onChange={(e) => onInputChange('volunteer_roles', e.target.value)}
                    className="min-h-[80px] text-base"
                    placeholder="Usher&#10;Greeter&#10;Worship Team&#10;Sound Tech"
                    rows={4}
                  />
                  <p className="text-sm text-gray-600 mt-1">
                    Enter each volunteer role on a separate line. These roles will be available when assigning volunteers.
                  </p>
                </div>
              )}
            </div>

            {/* Event Reminders Section */}
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Event Reminders</h3>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={isEditing ? "edit-enable-reminders" : "enable-reminders"}
                  checked={formData.enable_reminders}
                  onCheckedChange={(checked) => onInputChange('enable_reminders', checked)}
                />
                <Label htmlFor={isEditing ? "edit-enable-reminders" : "enable-reminders"}>Send automatic reminders</Label>
              </div>

              {formData.enable_reminders && (
                <div className="space-y-4 pl-6">
                  <div>
                    <Label>Reminder Type</Label>
                    <Select value={formData.reminder_type || 'sms'} onValueChange={(value) => onInputChange('reminder_type', value)}>
                      <SelectTrigger className="h-12 text-base">
                        <SelectValue placeholder="Select reminder type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sms">SMS Text Message</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Send reminder</Label>
                    <div className="grid grid-cols-3 gap-2 mt-1">
                      <Input
                        type="number"
                        min="1"
                        value={formData.reminder_timing_value || 24}
                        onChange={(e) => onInputChange('reminder_timing_value', parseInt(e.target.value) || 1)}
                        className="h-12 text-base"
                      />
                      <Select value={formData.reminder_timing_unit || 'hours'} onValueChange={(value) => onInputChange('reminder_timing_unit', value)}>
                        <SelectTrigger className="h-12 text-base">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="minutes">minutes</SelectItem>
                          <SelectItem value="hours">hours</SelectItem>
                          <SelectItem value="days">days</SelectItem>
                          <SelectItem value="weeks">weeks</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex items-center text-sm text-gray-600 px-2">
                        before event
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label>Send to</Label>
                    <Select value={formData.reminder_target || 'all'} onValueChange={(value) => onInputChange('reminder_target', value)}>
                      <SelectTrigger className="h-12 text-base">
                        <SelectValue placeholder="Select target audience" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All active members</SelectItem>
                        <SelectItem value="groups">Specific groups</SelectItem>
                        <SelectItem value="rsvp_attendees">Only RSVP attendees</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Group Selection */}
                  {formData.reminder_target === 'groups' && (
                    <div>
                      <Label>
                        Select Groups
                        {formData.reminder_target_groups.length > 0 && (
                          <span className="text-sm text-blue-600 ml-2">
                            ({formData.reminder_target_groups.length} selected)
                          </span>
                        )}
                      </Label>
                      <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-3 mt-1">
                        {groups.map((group) => (
                          <div key={group.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`group-${group.id}`}
                              checked={formData.reminder_target_groups.includes(group.id)}
                              onCheckedChange={(checked) => {
                                const updatedGroups = checked
                                  ? [...formData.reminder_target_groups, group.id]
                                  : formData.reminder_target_groups.filter(id => id !== group.id);
                                onInputChange('reminder_target_groups', updatedGroups);
                              }}
                            />
                            <Label htmlFor={`group-${group.id}`} className="text-sm cursor-pointer">
                              {group.name}
                              {group.description && (
                                <span className="text-gray-500 ml-1">({group.description})</span>
                              )}
                            </Label>
                          </div>
                        ))}
                      </div>
                      {formData.reminder_target_groups.length === 0 && (
                        <p className="text-sm text-orange-600 mt-1">Please select at least one group</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}